import { useState, useCallback, useEffect, useRef } from "react";
import {
  useRealtimeKitMeeting,
  useRealtimeKitSelector,
} from "@cloudflare/realtimekit-react";
import { Users } from "lucide-react";
import { MeetingLogoutButton } from "./MeetingLogoutButton";
import { LivestreamHostSidebar } from "./LivestreamHostSidebar";
import { LivestreamHostPreviewVideo } from "./LivestreamHostPreviewVideo";
import { LivestreamVideoGrid } from "./LivestreamVideoGrid";
import { LivestreamRandomParticipantOverlay } from "./LivestreamRandomParticipantOverlay";
import { LivestreamPinnedCommentOverlay } from "./LivestreamPinnedCommentOverlay";
import { LivestreamViewerFrameOverlay } from "./LivestreamViewerFrameOverlay";
import { useLivestreamChat } from "./useLivestreamChat";
import { useLivestreamPinnedComment } from "./useLivestreamPinnedComment";
import { useLivestreamRandomParticipant } from "./useLivestreamRandomParticipant";
import { useLivestreamStageFeeds } from "./useLivestreamStageFeeds";
import { useLivestreamRequestJoinVisibility } from "./useLivestreamRequestJoinVisibility";
import { useLivestreamStageHost } from "./useLivestreamStageHost";
import { useHostMediaLocalRecording } from "./useHostMediaLocalRecording";
import { useHostMediaStream } from "./useHostMediaStream";
import { useLivestreamMediaDevices } from "./useLivestreamMediaDevices";

export function LivestreamHostView({
  shopProductIds,
  showLogout,
  loggingOut,
  onLogout,
}: {
  shopProductIds: string[];
  showLogout: boolean;
  loggingOut: boolean;
  onLogout: () => void;
}) {
  const { meeting } = useRealtimeKitMeeting();
  const selfId = useRealtimeKitSelector((m) => m.self.id);
  const selfName = useRealtimeKitSelector((m) => m.self.name);
  const roomState = useRealtimeKitSelector((m) => m.self.roomState);
  const videoEnabled = useRealtimeKitSelector((m) => m.self.videoEnabled);
  const audioEnabled = useRealtimeKitSelector((m) => m.self.audioEnabled);
  const screenShareEnabled = useRealtimeKitSelector((m) => m.self.screenShareEnabled);
  const canShareScreen =
    useRealtimeKitSelector((m) => m.self.permissions.canProduceScreenshare) === "ALLOWED";
  const canRecord = useRealtimeKitSelector((m) => m.self.permissions.canRecord);
  const canKickParticipants = useRealtimeKitSelector(
    (m) => m.self.permissions.kickParticipant,
  );
  const recordingState = useRealtimeKitSelector((m) => m.recording.recordingState);
  const isRecordingActive =
    recordingState === "RECORDING" ||
    recordingState === "PAUSED" ||
    recordingState === "STARTING";
  const recordingBusy =
    recordingState === "STARTING" || recordingState === "STOPPING";
  const showLogoutButton = showLogout && roomState !== "joined";
  const [isLive, setIsLive] = useState(false);
  const [goingLive, setGoingLive] = useState(false);
  const [endingStream, setEndingStream] = useState(false);
  const [previewVideoEnabled, setPreviewVideoEnabled] = useState(true);
  const [previewAudioEnabled, setPreviewAudioEnabled] = useState(false);
  const mediaDevices = useLivestreamMediaDevices();
  const { videoRef, videoTrack, audioTrack } = useHostMediaStream({
    meeting,
    videoDeviceId: mediaDevices.selectedVideoDeviceId,
    audioDeviceId: mediaDevices.selectedAudioDeviceId,
    previewVideoEnabled: !isLive && previewVideoEnabled,
  });
  const prevVideoTrackIdRef = useRef<string | null>(null);
  const prevAudioTrackIdRef = useRef<string | null>(null);
  const { messages, sendMessage } = useLivestreamChat({ meeting });
  const { pinnedComment, togglePinComment } = useLivestreamPinnedComment({
    meeting,
    isHost: true,
    messages,
  });
  const hasShopProducts = shopProductIds.length > 0;
  const {
    requestJoinVisible,
    setRequestJoinVisible,
    shopVisible,
    setShopVisible,
  } = useLivestreamRequestJoinVisibility({
    meeting,
    isHost: true,
  });
  const stageFeeds = useLivestreamStageFeeds({ meeting, includeSelf: isLive });
  const {
    participants,
    stageRequests,
    grantAccess,
    denyAccess,
    removeFromStage,
    kickFromMeeting,
    actingOnUserId,
  } = useLivestreamStageHost({ meeting });
  const { activeDraw, clearActiveDraw, selectRandomParticipant } =
    useLivestreamRandomParticipant({ meeting });
  const { startHostRecording, stopHostRecording } = useHostMediaLocalRecording({
    meeting,
    videoTrack,
    audioTrack,
  });
  const [selectingRandomParticipant, setSelectingRandomParticipant] = useState(false);
  const eligibleRandomParticipantNames = participants
    .filter((participant) => !participant.isSelf)
    .map((participant) => participant.name);
  const hasStageScreenShare = stageFeeds.some(
    (participant) =>
      participant.screenShareEnabled && Boolean(participant.screenShareTracks?.video),
  );
  const showViewerFrameOverlay =
    isLive && stageFeeds.length === 1 && !hasStageScreenShare;
  const participantCount = participants.length;
  const effectiveVideoEnabled = isLive ? videoEnabled : previewVideoEnabled;
  const effectiveAudioEnabled = isLive ? audioEnabled : previewAudioEnabled;

  useEffect(() => {
    if (!meeting || !isLive) {
      prevVideoTrackIdRef.current = null;
      prevAudioTrackIdRef.current = null;
      return;
    }

    const videoTrackId = videoTrack?.id ?? null;
    const audioTrackId = audioTrack?.id ?? null;
    const videoChanged =
      prevVideoTrackIdRef.current !== null &&
      prevVideoTrackIdRef.current !== videoTrackId;
    const audioChanged =
      prevAudioTrackIdRef.current !== null &&
      prevAudioTrackIdRef.current !== audioTrackId;

    prevVideoTrackIdRef.current = videoTrackId;
    prevAudioTrackIdRef.current = audioTrackId;

    if (!videoChanged && !audioChanged) return;

    void (async () => {
      if (videoChanged && videoEnabled && videoTrack) {
        await meeting.self.enableVideo(videoTrack);
      }
      if (audioChanged && audioEnabled && audioTrack) {
        await meeting.self.enableAudio(audioTrack);
      }
    })();
  }, [meeting, isLive, videoTrack, audioTrack, videoEnabled, audioEnabled]);

  const handleGoLive = useCallback(async () => {
    if (!meeting || goingLive || isLive) return;
    setGoingLive(true);
    try {
      if (meeting.stage.status === "OFF_STAGE") {
        await meeting.stage.requestAccess();
      }
      if (meeting.stage.status === "ACCEPTED_TO_JOIN_STAGE") {
        await meeting.stage.join();
      }
      if (previewAudioEnabled && audioTrack) {
        await meeting.self.enableAudio(audioTrack);
      }
      if (previewVideoEnabled && videoTrack) {
        await meeting.self.enableVideo(videoTrack);
      }
      setIsLive(true);
    } finally {
      setGoingLive(false);
    }
  }, [
    audioTrack,
    goingLive,
    isLive,
    meeting,
    previewAudioEnabled,
    previewVideoEnabled,
    videoTrack,
  ]);

  const handleEndStream = useCallback(async () => {
    if (!meeting || endingStream || !isLive) return;
    setEndingStream(true);
    try {
      setPreviewAudioEnabled(audioEnabled);
      setPreviewVideoEnabled(videoEnabled);
      if (screenShareEnabled) {
        await meeting.self.disableScreenShare();
      }
      if (audioEnabled) {
        await meeting.self.disableAudio();
      }
      if (videoEnabled) {
        await meeting.self.disableVideo();
      }
      if (meeting.stage.status === "ON_STAGE") {
        await meeting.stage.leave();
      }
      setIsLive(false);
    } finally {
      setEndingStream(false);
    }
  }, [
    audioEnabled,
    endingStream,
    isLive,
    meeting,
    screenShareEnabled,
    videoEnabled,
  ]);

  if (!meeting) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-black text-white">
        Connecting...
      </div>
    );
  }

  return (
    <div className="flex h-dvh w-screen bg-black">
      <div className="@container relative min-w-0 flex-1 [container-type:size]">
        {showLogoutButton ? (
          <MeetingLogoutButton
            showLogout={showLogoutButton}
            loggingOut={loggingOut}
            onLogout={onLogout}
          />
        ) : null}
        {participantCount > 0 ? (
          <div
            className="pointer-events-none absolute top-[max(0.75rem,env(safe-area-inset-top))] left-3 z-20 flex items-center gap-1.5 rounded-full border border-white/20 bg-black/55 px-2.5 py-1 text-sm text-white backdrop-blur-sm sm:left-4"
            aria-label={`${participantCount} participant${participantCount === 1 ? "" : "s"}`}
          >
            <Users className="h-3.5 w-3.5" aria-hidden />
            <span>{participantCount}</span>
          </div>
        ) : null}
        {isLive ? (
          <LivestreamVideoGrid participants={stageFeeds} selfId={selfId} />
        ) : (
          <LivestreamHostPreviewVideo
            videoEnabled={previewVideoEnabled}
            name={selfName ?? ""}
            videoRef={videoRef}
          />
        )}
        <LivestreamRandomParticipantOverlay draw={activeDraw} onComplete={clearActiveDraw} />
        <LivestreamPinnedCommentOverlay
          pinnedComment={pinnedComment}
          className="bottom-[max(0.75rem,env(safe-area-inset-bottom))] sm:bottom-[max(0.75rem,env(safe-area-inset-bottom))]"
        />
        {showViewerFrameOverlay ? <LivestreamViewerFrameOverlay /> : null}
      </div>
      <LivestreamHostSidebar
        messages={messages}
        onSendMessage={sendMessage}
        pinnedComment={pinnedComment}
        onTogglePinComment={togglePinComment}
        participants={participants}
        stageRequests={stageRequests}
        onGrantStageAccess={async ({ userId }) => {
          if (hasStageScreenShare) return;
          await grantAccess({ userId });
        }}
        onDenyStageAccess={denyAccess}
        onRemoveFromStage={removeFromStage}
        onKickFromMeeting={kickFromMeeting}
        onSelectRandomParticipant={async () => {
          if (selectingRandomParticipant || eligibleRandomParticipantNames.length === 0) return;
          setSelectingRandomParticipant(true);
          try {
            await selectRandomParticipant({
              eligibleNames: eligibleRandomParticipantNames,
            });
          } finally {
            setSelectingRandomParticipant(false);
          }
        }}
        selectingRandomParticipant={selectingRandomParticipant}
        canSelectRandomParticipant={eligibleRandomParticipantNames.length > 0}
        actingOnUserId={actingOnUserId}
        canGrantStageAccess={!hasStageScreenShare}
        canKickParticipants={canKickParticipants}
        isLive={isLive}
        goingLive={goingLive}
        endingStream={endingStream}
        onGoLive={handleGoLive}
        onEndStream={handleEndStream}
        audioEnabled={effectiveAudioEnabled}
        videoEnabled={effectiveVideoEnabled}
        screenShareEnabled={screenShareEnabled}
        canShareScreen={canShareScreen && isLive}
        canRecord={canRecord && isLive}
        isRecordingActive={isRecordingActive}
        recordingBusy={recordingBusy}
        mediaDevices={mediaDevices}
        onToggleRecording={() => {
          if (!isLive || recordingBusy) return;
          if (isRecordingActive) {
            stopHostRecording();
            void meeting.recording.stop();
          } else {
            startHostRecording();
            void meeting.recording.start();
          }
        }}
        onToggleAudio={() => {
          if (isLive) {
            if (audioEnabled) {
              void meeting.self.disableAudio();
            } else if (audioTrack) {
              void meeting.self.enableAudio(audioTrack);
            }
            return;
          }
          setPreviewAudioEnabled((value) => !value);
        }}
        onToggleVideo={() => {
          if (isLive) {
            if (videoEnabled) {
              void meeting.self.disableVideo();
            } else if (videoTrack) {
              void meeting.self.enableVideo(videoTrack);
            }
            return;
          }
          setPreviewVideoEnabled((value) => !value);
        }}
        onToggleScreenShare={() => {
          if (!isLive) return;
          if (screenShareEnabled) {
            void meeting.self.disableScreenShare();
          } else {
            void meeting.self.enableScreenShare();
          }
        }}
        requestJoinVisible={requestJoinVisible}
        onRequestJoinVisibleChange={({ visible }) => {
          void setRequestJoinVisible({ visible });
        }}
        hasShopProducts={hasShopProducts}
        shopVisible={shopVisible}
        onShopVisibleChange={({ visible }) => {
          void setShopVisible({ visible });
        }}
      />
    </div>
  );
}
